/**
 * avatars/animals/animal-materials.js
 * Tạo hệ thống Material và Texture theo từng vùng giải phẫu (Vùng thân 6 mặt, Đầu mặt sạch, Bụng, Chân, Cánh, Đuôi)
 * Tái sử dụng và đăng ký tự động vào disposables.
 */

import * as THREE from 'three';
import { generateAnimalTexture, shadeColor } from '../animal-presets.js';

function createTextureMaterial(canvas, disposables, defaultColor, roughness = 0.8) {
  if (canvas) {
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    disposables.push(tex);
    const mat = new THREE.MeshStandardMaterial({
      map: tex,
      roughness
    });
    disposables.push(mat);
    return mat;
  }
  const mat = new THREE.MeshStandardMaterial({
    color: defaultColor,
    roughness
  });
  disposables.push(mat);
  return mat;
}

/**
 * Khởi tạo bộ materials theo từng vùng cho Động vật 4 chân & Gia cầm
 */
export function createAnimalMaterials(morph, variant, disposables, isAvian = false) {
  const v = variant;
  const patternType = v.patternType || 'solid_fur';

  // --- Màu sắc theo phân vùng ---
  const isSheep = !!morph.hasWoolLayer;
  const headBaseColor = isAvian ? v.headColor || v.baseColor : isSheep ? v.skinColor || v.baseColor : v.baseColor;
  const legColor = isAvian ? v.legColor || '#ea580c' : v.skinColor || v.baseColor;
  const bellyColor = v.bellyColor || shadeColor(v.baseColor, -5);

  // --- 1. Materials 6 mặt cho Thân mình (Body) ---
  // [+X (phải), -X (trái), +Y (lưng), -Y (bụng), +Z (ngực), -Z (đuôi)]
  const flankCanvas = generateAnimalTexture(patternType, {
    baseColor: v.baseColor,
    patternColor: v.patternColor,
    accentColor: v.accentColor,
    seed: 0
  });
  const flankMat = createTextureMaterial(flankCanvas, disposables, v.baseColor, 0.8);

  const chestCanvas = generateAnimalTexture(patternType, {
    baseColor: v.baseColor,
    patternColor: v.patternColor,
    accentColor: v.accentColor,
    seed: 1
  });
  const chestMat = createTextureMaterial(chestCanvas, disposables, v.baseColor, 0.8);

  const rearCanvas = generateAnimalTexture(patternType, {
    baseColor: v.baseColor,
    patternColor: v.patternColor,
    accentColor: v.accentColor,
    seed: 2
  });
  const rearMat = createTextureMaterial(rearCanvas, disposables, v.baseColor, 0.8);

  const bellyMat = new THREE.MeshStandardMaterial({
    color: bellyColor,
    roughness: 0.85
  });
  disposables.push(bellyMat);

  const bodyMaterials = [
    flankMat, // +X (phải)
    flankMat, // -X (trái)
    flankMat, // +Y (lưng)
    bellyMat, // -Y (bụng - dùng bellyColor)
    chestMat, // +Z (ngực trước - seed 1)
    rearMat // -Z (đuôi sau - seed 2)
  ];

  // --- 2. Materials 6 mặt cho Đầu (Head) ---
  // [+X, -X, +Y, -Y, +Z (mặt trước), -Z]
  const cleanFaceCanvas = generateAnimalTexture(patternType, {
    baseColor: headBaseColor,
    patternColor: v.patternColor,
    accentColor: v.accentColor,
    skinColor: v.skinColor,
    isCleanFace: true,
    seed: 0
  });
  const cleanFaceMat = createTextureMaterial(cleanFaceCanvas, disposables, headBaseColor, 0.85);

  const headSideCanvas = generateAnimalTexture(patternType, {
    baseColor: headBaseColor,
    patternColor: v.patternColor,
    accentColor: v.accentColor,
    seed: 3
  });
  const headSideMat = createTextureMaterial(headSideCanvas, disposables, headBaseColor, 0.85);

  const headMaterials = [
    headSideMat, // +X
    headSideMat, // -X
    headSideMat, // +Y
    cleanFaceMat, // -Y
    cleanFaceMat, // +Z (mặt trước sạch sẽ)
    headSideMat // -Z
  ];

  // --- 3. Các bộ phận chi tiết khác ---
  const snoutColor = isSheep ? v.skinColor || v.snoutColor || v.baseColor : v.snoutColor || v.baseColor;
  const snoutMat = new THREE.MeshStandardMaterial({
    color: snoutColor,
    roughness: 0.85
  });

  const noseMat = new THREE.MeshStandardMaterial({
    color: v.noseColor || '#0f172a',
    roughness: 0.6
  });

  const eyeMat = new THREE.MeshStandardMaterial({
    color: v.eyeColor || '#0f172a',
    roughness: 0.4
  });

  const eyeHighlightMat = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    roughness: 0.2
  });

  const blushMat = new THREE.MeshStandardMaterial({
    color: v.blushColor || '#fda4af',
    roughness: 0.9
  });

  const legMat = new THREE.MeshStandardMaterial({
    color: legColor,
    roughness: 0.85
  });

  const hornMat = new THREE.MeshStandardMaterial({
    color: v.hornColor || '#e2e8f0',
    roughness: 0.5
  });

  const maneMat = new THREE.MeshStandardMaterial({
    color: v.maneColor || v.patternColor || '#1e293b',
    roughness: 0.85
  });

  const combMat = new THREE.MeshStandardMaterial({
    color: v.combColor || '#ef4444',
    roughness: 0.7
  });

  const beakMat = new THREE.MeshStandardMaterial({
    color: v.beakColor || '#f59e0b',
    roughness: 0.75
  });

  const tailMat = new THREE.MeshStandardMaterial({
    color: v.tailColor || v.baseColor,
    roughness: 0.85
  });

  const wingMat = new THREE.MeshStandardMaterial({
    color: v.wingColor || v.baseColor,
    roughness: 0.85
  });

  disposables.push(
    snoutMat,
    noseMat,
    eyeMat,
    eyeHighlightMat,
    blushMat,
    legMat,
    hornMat,
    maneMat,
    combMat,
    beakMat,
    tailMat,
    wingMat
  );

  return {
    bodyMaterials,
    flankMat,
    headMaterials,
    cleanFaceMat,
    snoutMat,
    noseMat,
    eyeMat,
    eyeHighlightMat,
    blushMat,
    legMat,
    hornMat,
    maneMat,
    combMat,
    beakMat,
    tailMat,
    wingMat
  };
}
